// Cross-tab counter of open study viewers.
//
// Each open study runs in its own browser tab, and each tab holds 1 WebGL
// RenderingEngine context (webGlContextCount: 1 in CornerstoneService). Browsers
// cap live WebGL contexts at ~16 per process; past that the browser evicts the
// oldest tabs' contexts and their viewports go blank. There's no in-app way to
// raise that cap, so we at least WARN the user before they pile up too many studies.
//
// VAPT: this used to keep a heartbeat map in localStorage ("augmento_open_viewers").
// It now uses the browser's Web Locks API instead, so nothing is stored. Each viewer
// tab holds one lock named "augmento-viewer-<tabId>" for as long as it is open, and
// navigator.locks.query() lists the locks held by every tab of this origin, so the
// count is the number of those locks. The browser drops a tab's lock by itself when
// the tab closes, crashes or navigates away, so no heartbeat or pruning is needed.
// Web Locks need HTTPS (or localhost); without them the count is simply unavailable
// and no warning is logged - nothing breaks.

const LOCK_PREFIX = "augmento-viewer-";

// Warn once the number of concurrently-open viewers crosses this. Kept well
// below the browser's ~16 context cap to leave headroom for report/annotated-
// image engines and to warn before things actually break.
export const OPEN_VIEWER_WARN_THRESHOLD = 8;

const locksSupported = () =>
  typeof navigator !== "undefined" &&
  !!navigator.locks &&
  typeof navigator.locks.request === "function" &&
  typeof navigator.locks.query === "function";

/**
 * Number of study viewers open across all tabs, or null when it can't be known
 * (no Web Locks: plain http or an old browser).
 * Counts held AND pending viewer locks, so a tab whose lock is still being granted
 * (the first few milliseconds after it opens) is already included.
 */
export const getOpenViewerCount = async () => {
  if (!locksSupported()) return null;
  try {
    const { held = [], pending = [] } = await navigator.locks.query();
    const names = new Set(
      [...held, ...pending]
        .map((lock) => lock && lock.name)
        .filter((name) => typeof name === "string" && name.startsWith(LOCK_PREFIX))
    );
    return names.size;
  } catch (e) {
    console.warn("[OpenViewerRegistry] could not count open viewers", e);
    return null;
  }
};

/**
 * Register this tab as an open viewer: hold a uniquely named lock until the returned
 * unregister function runs (unmount) or the tab goes away (the browser releases it).
 */
export const registerOpenViewer = () => {
  if (!locksSupported()) {
    console.warn("[OpenViewerRegistry] Web Locks unavailable (non-secure context or old browser) - open-viewer count disabled");
    return () => {};
  }

  let tabId;
  try {
    tabId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  } catch (_) {
    tabId = `${Date.now()}`;
  }

  let release = null;
  let unregistered = false;

  navigator.locks
    .request(LOCK_PREFIX + tabId, () =>
      new Promise((resolve) => {
        // Unregistered before the lock was even granted (fast mount/unmount): let it go now.
        if (unregistered) {
          resolve();
          return;
        }
        release = resolve; // held until unregister() or the tab closes
      })
    )
    .catch((e) => console.warn("[OpenViewerRegistry] could not register this viewer", e));

  return () => {
    unregistered = true;
    if (release) {
      release();
      release = null;
    }
  };
};

// OLD (localStorage heartbeat map, replaced by the Web Locks version above):
// // Same-origin tabs share localStorage. Each viewer tab writes a heartbeat
// // timestamp under its own id and prunes stale entries, so getOpenViewerCount()
// // reflects the number of currently-live viewer tabs (a tab that crashed or was
// // closed without cleanup simply ages out).
//
// const STORAGE_KEY = "augmento_open_viewers";
// const HEARTBEAT_MS = 3000;
// const STALE_MS = 9000; // ~3 missed heartbeats → treat the tab as gone
//
// const nowMs = () => Date.now();
//
// const readMap = () => {
//   try {
//     const raw = localStorage.getItem(STORAGE_KEY);
//     if (!raw) return {};
//     const parsed = JSON.parse(raw);
//     return parsed && typeof parsed === "object" ? parsed : {};
//   } catch (_) {
//     return {};
//   }
// };
//
// const writeMap = (map) => {
//   try {
//     localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
//   } catch (_) {
//     /* storage unavailable / quota — non-fatal, counting just degrades */
//   }
// };
//
// const prune = (map) => {
//   const cutoff = nowMs() - STALE_MS;
//   const out = {};
//   for (const id in map) {
//     if (typeof map[id] === "number" && map[id] >= cutoff) out[id] = map[id];
//   }
//   return out;
// };
//
// export const getOpenViewerCount = () => Object.keys(prune(readMap())).length;
//
// /**
//  * Register this tab as an open viewer (heartbeat + cleanup on close).
//  * Returns an unregister function to call on unmount.
//  */
// export const registerOpenViewer = () => {
//   let tabId;
//   try {
//     tabId = `${nowMs()}_${Math.random().toString(36).slice(2)}`;
//   } catch (_) {
//     tabId = `${nowMs()}`;
//   }
//
//   const beat = () => {
//     const map = prune(readMap());
//     map[tabId] = nowMs();
//     writeMap(map);
//   };
//
//   const remove = () => {
//     const map = prune(readMap());
//     delete map[tabId];
//     writeMap(map);
//   };
//
//   beat();
//   let interval = null;
//   try {
//     interval = setInterval(beat, HEARTBEAT_MS);
//   } catch (_) {
//     /* ignore */
//   }
//
//   const onUnload = () => remove();
//   try {
//     window.addEventListener("beforeunload", onUnload);
//     window.addEventListener("pagehide", onUnload);
//   } catch (_) {
//     /* ignore */
//   }
//
//   return () => {
//     if (interval) clearInterval(interval);
//     try {
//       window.removeEventListener("beforeunload", onUnload);
//       window.removeEventListener("pagehide", onUnload);
//     } catch (_) {
//       /* ignore */
//     }
//     remove();
//   };
// };
